import type { LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

/**
 * Headline KPI card. Composed of:
 *   - Icon chip (top-left)
 *   - Big value
 *   - Label (small caps)
 *   - Optional delta vs previous period (with direction arrow + color)
 *   - Optional sparkline OR sub-line (helper text)
 */

export interface StatCardProps {
  label: string;
  value: string | number;
  hint?: string;
  icon?: LucideIcon;
  /** Direction: + = good, - = bad, 0 = neutral. */
  delta?: { value: string; direction: "up" | "down" | "flat"; period?: string };
  tone?: "neutral" | "brand" | "accent" | "warn" | "danger";
  sparkline?: React.ReactNode;
  /** Optional click handler — turns the card into a button. */
  onClick?: () => void;
}

const TONE_ICON_BG: Record<NonNullable<StatCardProps["tone"]>, string> = {
  neutral: "bg-line-soft text-muted",
  brand: "bg-brand-50 text-brand-700",
  accent: "bg-accent-soft text-emerald-700",
  warn: "bg-warn-soft text-amber-700",
  danger: "bg-danger-soft text-red-700",
};

export function StatCard({ label, value, hint, icon: Icon, delta, tone = "neutral", sparkline, onClick }: StatCardProps) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        {Icon && (
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${TONE_ICON_BG[tone]}`}>
            <Icon className="h-4.5 w-4.5" strokeWidth={2.2} />
          </div>
        )}
        {delta && <DeltaBadge delta={delta} />}
      </div>
      <div className="mt-4">
        <div className="text-2xs font-medium uppercase tracking-wider text-muted">{label}</div>
        <div className="mt-1 text-3xl font-semibold leading-none text-ink">{value}</div>
        {hint && <div className="mt-1.5 text-xs text-muted">{hint}</div>}
      </div>
      {sparkline && <div className="mt-3">{sparkline}</div>}
    </>
  );

  if (onClick) {
    return (
      <button onClick={onClick} className="card card-hover w-full p-5 text-left">
        {inner}
      </button>
    );
  }
  return <div className="card p-5">{inner}</div>;
}

function DeltaBadge({ delta }: { delta: NonNullable<StatCardProps["delta"]> }) {
  const palette =
    delta.direction === "up"
      ? "bg-accent-soft text-emerald-700"
      : delta.direction === "down"
      ? "bg-danger-soft text-red-700"
      : "bg-line-soft text-muted";
  const Icon =
    delta.direction === "up" ? ArrowUpRight :
    delta.direction === "down" ? ArrowDownRight :
    Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-2xs font-semibold ${palette}`}>
      <Icon className="h-3 w-3" strokeWidth={2.4} />
      {delta.value}
      {delta.period && <span className="ml-0.5 font-normal opacity-70">{delta.period}</span>}
    </span>
  );
}
